import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ITariffZone,
  CreateTariffZoneDto,
  UpdateTariffZoneDto,
  ZoneImpact,
  ZoneImpactRequest,
  ZoneTestResult,
} from '@carrierbridge/shared';
import api from '@/lib/api';
import { bulkDeleteInBatches } from '@/lib/bulk-delete';

const TARIFF_ZONES_KEY = ['tariff-zones'] as const;

/**
 * La carte de couverture d'une grille dessine les zones que ses lignes citent :
 * toute écriture sur une zone la rend périmée, comme le référentiel lui-même.
 */
const GRID_COVERAGE_KEY = ['version-coverage'] as const;

export function useTariffZones() {
  return useQuery<ITariffZone[]>({
    queryKey: TARIFF_ZONES_KEY,
    queryFn: async () => {
      const { data } = await api.get<ITariffZone[]>('/tariff-zones');
      return data;
    },
  });
}

export function useCreateTariffZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateTariffZoneDto) => {
      const { data } = await api.post<ITariffZone>('/tariff-zones', dto);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TARIFF_ZONES_KEY });
      qc.invalidateQueries({ queryKey: GRID_COVERAGE_KEY });
    },
  });
}

export function useUpdateTariffZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateTariffZoneDto }) => {
      const { data } = await api.patch<ITariffZone>(`/tariff-zones/${id}`, dto);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TARIFF_ZONES_KEY });
      qc.invalidateQueries({ queryKey: GRID_COVERAGE_KEY });
    },
  });
}

export function useDeleteTariffZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/tariff-zones/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TARIFF_ZONES_KEY });
      qc.invalidateQueries({ queryKey: GRID_COVERAGE_KEY });
    },
  });
}

/**
 * Suppression en masse depuis l'onglet Zones de la console Data. Le lot est
 * découpé aux bornes que l'API accepte ({@link bulkDeleteInBatches}) : le
 * serveur renvoie ce qui est parti et ce qui a résisté, l'appelant décide du
 * message.
 *
 * Le cache est rafraîchi même si un lot échoue en cours de route : les lots
 * précédents sont réellement partis, le tableau ne doit pas les afficher encore.
 */
export function useBulkDeleteTariffZones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteInBatches('/tariff-zones', ids),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: TARIFF_ZONES_KEY });
      qc.invalidateQueries({ queryKey: GRID_COVERAGE_KEY });
    },
  });
}

export function useTestTariffZones() {
  return useMutation({
    mutationFn: async (body: {
      postalCode?: string;
      countryIso3?: string;
      department?: string;
      lat?: number;
      lng?: number;
    }) => {
      const { data } = await api.post<ZoneTestResult>('/tariff-zones/test', body);
      return data;
    },
  });
}

/**
 * Ce qu'une définition de zone touche : grilles qui la citent, zones sœurs du
 * même découpage, volume de référentiel couvert.
 *
 * Interroge une DÉFINITION, pas un id : le récap répond donc aussi pour une
 * zone en cours de création. L'appelant est chargé de ne fournir un `request`
 * stabilisé (débounce) qu'une fois la saisie posée — le tracé voyage dans le
 * corps de la requête.
 */
export function useZoneImpact(request: ZoneImpactRequest | null) {
  return useQuery<ZoneImpact>({
    // Signature courte plutôt que la définition entière : un contour de
    // département pèse des milliers de points, et react-query sérialise la clé
    // à chaque rendu.
    queryKey: ['tariff-zones', 'impact', request ? zoneImpactSignature(request) : null],
    queryFn: async () => {
      const { data } = await api.post<ZoneImpact>('/tariff-zones/impact', request);
      return data;
    },
    enabled: !!request && !!request.code.trim(),
    staleTime: 60_000,
    // Encart indicatif : une erreur s'affiche « indisponible » et ne relance
    // pas une requête coûteuse en boucle.
    retry: false,
  });
}

/** Signature stable et courte d'une définition de zone (clé de cache). */
function zoneImpactSignature(request: ZoneImpactRequest): string {
  const geo = request.polygon;
  let shape = 'none';
  if (geo) {
    const rings: number[][][] =
      geo.type === 'MultiPolygon'
        ? (geo.coordinates as number[][][][]).flat()
        : (geo.coordinates as number[][][]);
    let points = 0;
    let sumLat = 0;
    let sumLng = 0;
    for (const ring of rings) {
      points += ring.length;
      for (const [lng, lat] of ring) {
        sumLat += lat;
        sumLng += lng;
      }
    }
    // Nombre de sommets + barycentre : deux tracés distincts ne se confondent
    // pas en pratique, et la clé reste d'une longueur fixe.
    shape = `${points}:${(sumLat / (points || 1)).toFixed(4)}:${(sumLng / (points || 1)).toFixed(4)}`;
  }
  return [
    request.code.trim().toUpperCase(),
    (request.countryIso3 || 'FRA').toUpperCase(),
    (request.postalCodes || []).join(','),
    (request.postalPrefixes || []).join(','),
    (request.departments || []).join(','),
    shape,
  ].join('|');
}

export interface BulkZoneRadiusResult {
  updated: number;
  /** Zones laissées intactes, avec le motif — un contour dessiné, par exemple. */
  skipped: { id: string; code: string; reason: string }[];
}

/**
 * Rejoue le rayon d'un lot de zones circulaires, chacune autour de son centre.
 *
 * Un seul appel plutôt qu'une requête par zone : un lot porte couramment les
 * quatre-vingt-dix zones créées par un import de grille, et le serveur les
 * écrit en une fois.
 */
export function useBulkZoneRadius() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { ids: string[]; radiusKm: number }) => {
      const { data } = await api.post<BulkZoneRadiusResult>('/tariff-zones/bulk-radius', dto);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TARIFF_ZONES_KEY });
      qc.invalidateQueries({ queryKey: GRID_COVERAGE_KEY });
    },
  });
}

/** Départements (préfixes de CP) d'un pays hors France, pour le sélecteur de cotation. */
export function useCountryDepartments(countryIso3: string, enabled = true) {
  return useQuery<{ value: string; label: string }[]>({
    queryKey: ['tariff-zones', 'country-departments', countryIso3],
    queryFn: async () => {
      const { data } = await api.get<{ value: string; label: string }[]>(
        '/tariff-zones/country-departments',
        { params: { country: countryIso3 } },
      );
      return data;
    },
    enabled: enabled && !!countryIso3 && countryIso3 !== 'FRA',
    staleTime: Infinity,
  });
}
